<!-- NOTE: slot text should be wrapped in a <span> to avoid default styles (e.g. <p>) -->
<script setup lang="ts">
import { onKeyStroke, onClickOutside } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, useSlots } from 'vue';

import AppOverlay from '../AppOverlay.vue';
import { isSmallModal } from '../modals.helper';
import AppButton from '../../appButton/AppButton.vue';
import { KeyboardKeyCode } from '../../../models';
import {
  blockBackgroundScroll,
  enableBackgroundScroll,
} from '../../../helpers/blockBackgroundScroll';

interface AppStandardModalProps {
  isButtonDisabled?: boolean;
  footerPadding?: 'small' | 'large';
}

const props = withDefaults(defineProps<AppStandardModalProps>(), {
  isButtonDisabled: false,
  footerPadding: 'large',
});

export interface AppStandardModalEmits {
  (eventName: 'close'): void;
  (eventName: 'confirm'): void;
}

const emit = defineEmits<AppStandardModalEmits>();

const standardModalRef = ref<HTMLDivElement | null>(null);

onClickOutside(standardModalRef, (event) => {
  const dropdown = document.querySelector('#appSelectDropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }

  emit('close');
});

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

// INFO: empty string in slot is treated as a slot with no content
const slots = useSlots();

const hasTitle = computed(
  () => !!slots['title'] && !!slots['title']()[0]?.children
);

const hasSubtitle = computed(
  () => !!slots['subtitle'] && !!slots['subtitle']()[0]?.children
);

const hasButton = computed(
  () => !!slots['button'] && !!slots['button']()[0]?.children
);

const hasSummary = computed(() => !!slots['summary']);

onMounted(() => {
  blockBackgroundScroll();
});

onUnmounted(() => enableBackgroundScroll());
</script>

<template>
  <AppOverlay :is-modal="true" @close="emit('close')">
    <div ref="standardModalRef" class="app-standard-modal">
      <header v-if="hasTitle" class="modal-header">
        <div class="main-info" :class="{ centered: !hasSubtitle }">
          <div class="title">
            <slot name="title" />
          </div>
          <div v-if="hasSubtitle" class="subtitle">
            <slot name="subtitle" />
          </div>
        </div>
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          :icon="{ name: 'close' }"
          @click="emit('close')"
        />
      </header>
      <aside class="modal-body" tabindex="0">
        <slot name="body" />
      </aside>
      <footer
        v-if="hasButton || hasSummary"
        class="modal-footer"
        :class="{
          left: hasSummary,
          [props.footerPadding]: true,
        }"
      >
        <AppButton
          v-if="hasButton"
          :state="props.isButtonDisabled ? 'disabled' : 'enabled'"
          class="button"
          type="primary"
          size="large"
          :wide="isSmallModal"
          @click="emit('confirm')"
        >
          <slot name="button" />
        </AppButton>
        <div v-else-if="hasSummary" class="modal-footer-summary">
          <slot name="summary" />
        </div>
      </footer>
    </div>
  </AppOverlay>
</template>

<style lang="scss" scoped>
@import '../../../styles/media';
@import '../../../styles/_fonts.scss';

.app-standard-modal {
  --padding: var(--spacing-24);
  --small-padding: var(--spacing-16);

  --max-width: 620px;
  --max-height: 737px;
  --min-height: 478px;

  display: flex;
  flex-flow: column;

  width: 100%;
  max-width: var(--max-width);
  height: 100%;
  min-height: var(--min-height);
  max-height: var(--max-height);

  padding: var(--padding);
  box-sizing: border-box;
  z-index: var(--modal-z-index);
  background-color: var(--color-white);
  border-radius: var(--border-radius-12);
  overflow: hidden;

  @include for-less-or-equal-than-900-px {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    padding: var(--padding) var(--small-padding);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    padding-bottom: var(--spacing-16);

    .main-info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      &.centered {
        justify-content: center;
      }
      .title {
        color: var(--color-neutral-70);
        @include font-label-xl-bold;
      }
      .subtitle {
        color: var(--color-neutral-70);
        @include font-body-l-light;
      }
    }
  }

  .modal-body {
    position: relative;
    overflow: auto;
    flex-grow: 1;
    margin: 0;
    // NOTE: to position scroll at the edge
    margin-right: calc(var(--padding) * (-1));
    padding-right: var(--padding);
  }

  .visible {
    overflow: visible;
  }

  .modal-footer {
    display: flex;
    justify-content: center;

    // NOTE: to make the top border take up the entire modal width
    margin-bottom: calc(var(--padding) * (-1));
    margin-left: calc(var(--padding) * (-1));
    margin-right: calc(var(--padding) * (-1));
    border-top: 1px solid var(--color-neutral-30);

    &.small {
      padding: var(--spacing-16) var(--padding) var(--spacing-16) var(--padding);
    }

    &.large {
      padding: var(--spacing-24) var(--padding) var(--spacing-32) var(--padding);
    }

    &.left {
      justify-content: start;
    }
    .button {
      min-width: 300px;
    }

    .modal-footer-summary {
      display: flex;
      justify-content: flex-start;

      // NOTE: to make the top border take up the entire modal width
      width: calc(100% + 48px);
      margin-left: calc(var(--padding) * (-1));
      margin-right: calc(var(--padding) * (-1));
      padding-left: var(--padding);
      padding-right: var(--padding);
    }
  }
}
</style>
